import { Card, ErrorMessage, Input } from "@";
import {
  DocumentCheckIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import type { ProjectLicenceSectionProps } from "./types";
import type { ProjectLicenceCode } from "../../types";
import {
  InfoCallout,
  RepeaterAddButton,
  RepeaterClearAll,
  RepeaterEmpty,
  RepeaterItem,
} from "../RepeaterPrimitives";
import React from "react";

const defaultLicenceCode: ProjectLicenceCode = {
  code: "",
  startDate: "",
  endDate: "",
};

export const ProjectLicenceSection = ({
  watch,
  setValue,
  errors,
  show,
}: ProjectLicenceSectionProps) => {
  const licences = (watch("projectLicenceCodes") as ProjectLicenceCode[]) || [];

  const addLicence = () => {
    setValue("projectLicenceCodes", [...licences, { ...defaultLicenceCode }]);
  };

  const removeLicence = (index: number) => {
    const updated = licences.filter((_, i) => i !== index);
    setValue("projectLicenceCodes", updated);
  };

  const updateLicence = (
    index: number,
    field: keyof ProjectLicenceCode,
    value: string,
  ) => {
    const updated = licences.map((item, i) =>
      i === index ? { ...item, [field]: value } : item,
    );
    setValue("projectLicenceCodes", updated);
  };

  return (
    <>
      {errors?.projectLicenceCodes && (
        <ErrorMessage value="يوجد خطأ في إعدادات الترخيص" />
      )}
      <Card
        isOpen={false}
        className="lg:col-span-2"
        title={`تقارير الجمع للمركز الوطني${licences.length > 0 ? ` (${licences.length})` : ""}`}
      >
        <InfoCallout title="ملاحظات" icon={InformationCircleIcon} tone="info">
          <ul className="list-inside list-disc space-y-1">
            <li>رقم الترخيص سوف يظهر في تقرير الجمع للمركز الوطني</li>
            <li>
              تاريخ بداية ونهاية الترخيص سوف يحدد رقم الترخيص الذي سوف يظهر في
              تقرير الجمع للمركز الوطني
            </li>
          </ul>
        </InfoCallout>

        {licences.length === 0 && !show && (
          <RepeaterEmpty
            icon={DocumentCheckIcon}
            title="لا توجد تراخيص مسجلة"
            description="أضف ترخيصًا واحدًا أو أكثر لتظهر في تقارير الجمع"
          />
        )}

        {licences.length > 0 && (
          <div className="mb-5 space-y-4">
            {licences.map((licence, index) => (
              <RepeaterItem
                key={index}
                index={index}
                label={`الترخيص ${index + 1}`}
                show={show}
                onRemove={() => removeLicence(index)}
              >
                <div className="grid gap-4 md:grid-cols-3">
                  <Input
                    label="رقم الترخيص"
                    disabled={!!show}
                    value={licence.code}
                    change={(e: React.ChangeEvent<HTMLInputElement>) =>
                      updateLicence(index, "code", e.target.value)
                    }
                    error={errors?.projectLicenceCodes?.[index]?.code}
                  />
                  <Input
                    star
                    disabled={!!show}
                    type="date-range"
                    style="col-span-2"
                    labelA="تاريخ بداية الترخيص"
                    labelB="تاريخ نهاية الترخيص"
                    valueB={licence.endDate ? new Date(licence.endDate) : null}
                    valueA={
                      licence.startDate ? new Date(licence.startDate) : null
                    }
                    changeA={(date: Date | null) =>
                      updateLicence(
                        index,
                        "startDate",
                        date ? date.toISOString() : "",
                      )
                    }
                    changeB={(date: Date | null) =>
                      updateLicence(
                        index,
                        "endDate",
                        date ? date.toISOString() : "",
                      )
                    }
                    errorA={errors?.projectLicenceCodes?.[index]?.startDate}
                    errorB={errors?.projectLicenceCodes?.[index]?.endDate}
                  />
                </div>
              </RepeaterItem>
            ))}
          </div>
        )}

        {!show && (
          <div className="space-y-3">
            <RepeaterAddButton
              onClick={addLicence}
              label="إضافة ترخيص"
              hint="رقم الترخيص مع تاريخ بدايته ونهايته"
            />
            {licences.length > 1 && (
              <div className="flex justify-end">
                <RepeaterClearAll
                  onClick={() => setValue("projectLicenceCodes", [])}
                  label="حذف جميع التراخيص"
                />
              </div>
            )}
          </div>
        )}
      </Card>
    </>
  );
};
